/**
 * VirtClusterForm — Odoo-style full-screen detail view
 * Tabs: General / Nodos
 * SmartButtons: Tipo, IP gestión, Nodos, HA, HCI, Estado
 */

import React, { useEffect, useState } from 'react';
import {
  ArrowLeftIcon,
  CheckIcon,
  XMarkIcon,
  CircleStackIcon,
  GlobeAltIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  CpuChipIcon,
  PlusIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { registroGuardado } from '../utils/savedRecord';
import DocSmartButton from './DocSmartButton';
import CiActivityPanel from './common/CiActivityPanel';
import FormCardsShell, { Sf2Card } from './FormCardsShell';
import FormCardsHead from './FormCardsHead';
import FormPager from './common/FormPager';
import FormDirtyDock from './common/FormDirtyDock';
import useNavGuard from '../hooks/useNavGuard';
import '../styles/DetailView.css';
import './ServerFormCards.css';
import TabButton from './TabButton';
import DetailCrumb from './DetailCrumb';

const CLUSTER_TYPES = [
  { value: 'vmware_vsphere',    label: 'VMware vSphere',    icon: '🟦' },
  { value: 'proxmox',          label: 'Proxmox VE Cluster', icon: '🟧' },
  { value: 'hyper_v_failover', label: 'Hyper-V Failover',   icon: '🪟' },
  { value: 'nutanix',         label: 'Nutanix',             icon: '🔴' },
  { value: 'kvm_ovirt',       label: 'KVM / oVirt',         icon: '🐧' },
  { value: 'other',           label: 'Otro',                icon: '⬜' },
];

const MEMBER_ROLES = [
  { value: 'master',  label: 'Master / vCenter' },
  { value: 'worker',  label: 'Worker' },
  { value: 'witness', label: 'Witness' },
];

const STATUS_COLORS = {
  active:      { bg: 'rgba(16,185,129,.15)',  color: '#6ee7b7', label: 'Activo' },
  degraded:    { bg: 'rgba(234,179,8,.15)',   color: '#fde68a', label: 'Degradado' },
  maintenance: { bg: 'rgba(59,130,246,.15)',  color: '#93c5fd', label: 'Mantenimiento' },
  decom:       { bg: 'rgba(239,68,68,.15)',   color: '#fca5a5', label: 'Decom' },
};

const EMPTY_FORM = {
  name: '', cluster_type: 'vmware_vsphere', version: '',
  mgmt_ip: '', mgmt_url: '', total_nodes: '',
  is_hyperconverged: false, ha_enabled: false, drs_enabled: false,
  status: 'active', notes: '',
};

export default function VirtClusterForm({ cluster, companyId, onClose, onSave, initialMode, variant = 'classic', navItems = [], navIndex = -1, onNavigate }) {
  const isCards = variant === 'cards';
  const isEditing = Boolean(cluster?.id);
  /* La ficha se abre EN LECTURA (clic en la fila del listado); 'create' y 'edit'
     editan. Sin `initialMode` edita, como siempre. */
  const [editing, setEditing] = useState(initialMode ? initialMode !== 'view' : true);

  const [form, setForm]     = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [savedOk, setSavedOk] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  // Cambia para recargar el formulario desde el clúster guardado.
  const [reloadKey, setReloadKey] = useState(0);
  const [error, setError]   = useState(null);
  const [tab, setTab]       = useState('general');

  // Nodos (members)
  const [members, setMembers]         = useState([]);
  const [hypervisors, setHypervisors] = useState([]);
  const [addMember, setAddMember]     = useState({ hypId: '', role: 'worker' });
  const [addingMember, setAddingMember] = useState(false);

  // ── Bootstrap ──
  useEffect(() => {
    if (cluster) {
      setForm({
        name:              cluster.name || '',
        cluster_type:      cluster.cluster_type || 'vmware_vsphere',
        version:           cluster.version || '',
        mgmt_ip:           cluster.mgmt_ip || '',
        mgmt_url:          cluster.mgmt_url || '',
        total_nodes:       cluster.total_nodes ?? '',
        is_hyperconverged: !!cluster.is_hyperconverged,
        ha_enabled:        !!cluster.ha_enabled,
        drs_enabled:       !!cluster.drs_enabled,
        status:            cluster.status || 'active',
        notes:             cluster.notes || '',
      });
      setMembers(cluster.nodes || []);
    } else {
      setForm(EMPTY_FORM);
      setMembers([]);
    }
  }, [cluster, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.getHypervisors(companyId)
      .then(r => setHypervisors(r.data || []))
      .catch(() => {});
  }, [companyId]);

  const sf = (k, v) => { setForm(f => ({ ...f, [k]: v })); setIsDirty(true); };

  /* Descartar: recarga los valores guardados (no cierra la ficha). */
  const handleDiscard = () => {
    setReloadKey(k => k + 1);
    setIsDirty(false);
    setError(null);
  };

  // Cambios sin guardar al pasar de ficha o cerrar.
  const navGuard = useNavGuard({
    dirty: isDirty,
    saving,
    entity: 'este clúster',
    actionLabel: 'pasas a otro clúster o cierras la ficha',
    save: () => handleSave(),
  });

  // ── Save ──
  const handleSave = async () => {
    if (!form.name.trim()) { setError('El nombre es obligatorio'); return false; }
    try {
      setSaving(true); setError(null);
      const payload = {
        ...form,
        total_nodes: form.total_nodes !== '' ? Number(form.total_nodes) : null,
      };
      let creado = null;
      if (isEditing) await api.updateCluster(cluster.id, payload, companyId);
      else           creado = registroGuardado(await api.createCluster(payload, companyId));
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
      // Alta: el listado adopta el registro creado y la ficha sigue abierta ya
      // con id, así se encienden las pestañas que necesitan estar guardado.
      onSave?.(creado);
      setIsDirty(false);
      return true;
    } catch (e) { setError(e.message); return false; }
    finally { setSaving(false); }
  };

  // ── Members ──
  const handleAddMember = async () => {
    if (!addMember.hypId || !cluster?.id) return;
    try {
      setAddingMember(true);
      await api.addClusterMember(cluster.id, { hypervisor_id: Number(addMember.hypId), member_role: addMember.role }, companyId);
      setAddMember({ hypId: '', role: 'worker' });
      const r = await api.getClusters(companyId);
      const updated = (r.data || []).find(c => c.id === cluster.id);
      if (updated) setMembers(updated.nodes || []);
    } catch (e) { setError(e.message); }
    finally { setAddingMember(false); }
  };

  const handleRemoveMember = async (hypId) => {
    if (!cluster?.id) return;
    try {
      await api.removeClusterMember(cluster.id, hypId, companyId);
      setMembers(prev => prev.filter(m => m.hypervisor_id !== hypId));
    } catch (e) { setError(e.message); }
  };

  // ── Derived ──
  const typeInfo    = CLUSTER_TYPES.find(t => t.value === form.cluster_type);
  const statusInfo  = STATUS_COLORS[form.status] || STATUS_COLORS.active;
  const nodeCount   = members.length || form.total_nodes || 0;

  return (
    <div className="dv-overlay">
      {/* ── TopBar ── */}
      <div className="dv-topbar">
        <button className="dv-back-btn" onClick={() => navGuard.request(() => onClose())}>
          <ArrowLeftIcon />
          Clusters
        </button>
        <DetailCrumb label={form.name || 'Nuevo cluster'} />
        <FormPager
          items={navItems}
          index={navIndex}
          label="Clúster"
          disabled={saving}
          onChange={(destino) => navGuard.request(() => { setIsDirty(false); onNavigate?.(destino); })}
        />
        {navGuard.dialog}
        <div className="dv-topbar-spacer" />
        {savedOk && (
          <span style={{ color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>✓ Guardado</span>
        )}
        {!isCards && (
        <span className="dv-status-badge" style={{ background: statusInfo.bg, color: statusInfo.color }}>
          {statusInfo.label}
        </span>
        )}
        <div className="dv-topbar-actions">
          {error && <span style={{ color: '#ef4444', fontSize: 12, marginRight: 8 }}>{error}</span>}
          {isCards ? (
            <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
              <XMarkIcon /> Cerrar
            </button>
          ) : !editing ? (
            <>
              <button className="dv-btn dv-btn-secondary" type="button" onClick={onClose}>
                <XMarkIcon /> Cerrar
              </button>
              <button className="dv-btn dv-btn-primary" type="button" onClick={() => setEditing(true)}>
                <CheckIcon /> Editar
              </button>
            </>
          ) : (
            <>
              <button className="dv-btn dv-btn-secondary" onClick={onClose} type="button">
                <XMarkIcon /> Descartar
              </button>
              <button className="dv-btn dv-btn-primary" onClick={handleSave} disabled={saving} type="button">
                <CheckIcon /> {saving ? 'Guardando…' : 'Guardar'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── SmartBar (solo en la ficha clásica) ── */}
      {!isCards && (
      <div className="dv-smartbar">
        <button className="dv-smart-btn" style={{ borderColor: '#c7d2fe' }}>
          <span className="dv-smart-btn-icon"><CircleStackIcon style={{ width: 18, height: 18, color: '#6366f1' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#6366f1', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{typeInfo?.icon} {typeInfo?.label || form.cluster_type}</span>
          <span className="dv-smart-btn-label">Tipo</span>
        </button>

        <button className="dv-smart-btn" style={{ borderColor: '#bfdbfe' }}>
          <span className="dv-smart-btn-icon"><GlobeAltIcon style={{ width: 18, height: 18, color: '#3b82f6' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#3b82f6', fontSize: '0.82rem', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{form.mgmt_ip || '—'}</span>
          <span className="dv-smart-btn-label">IP gestión</span>
        </button>

        <button className="dv-smart-btn" style={{ borderColor: '#bbf7d0' }}>
          <span className="dv-smart-btn-icon"><ServerStackIcon style={{ width: 18, height: 18, color: '#16a34a' }} /></span>
          <span className="dv-smart-btn-value" style={{ color: '#16a34a' }}>{nodeCount}</span>
          <span className="dv-smart-btn-label">Nodos</span>
        </button>

        {form.ha_enabled && (
          <button className="dv-smart-btn" style={{ borderColor: '#fde68a' }}>
            <span className="dv-smart-btn-icon"><ShieldCheckIcon style={{ width: 18, height: 18, color: '#d97706' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#d97706', fontSize: '0.82rem' }}>HA</span>
            <span className="dv-smart-btn-label">Alta disponibilidad</span>
          </button>
        )}

        {form.is_hyperconverged && (
          <button className="dv-smart-btn" style={{ borderColor: '#ddd6fe' }}>
            <span className="dv-smart-btn-icon"><CpuChipIcon style={{ width: 18, height: 18, color: '#7c3aed' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#7c3aed', fontSize: '0.82rem' }}>HCI</span>
            <span className="dv-smart-btn-label">Hiperconvergente</span>
          </button>
        )}

        {form.drs_enabled && (
          <button className="dv-smart-btn" style={{ borderColor: '#ccfbf1' }}>
            <span className="dv-smart-btn-icon"><CpuChipIcon style={{ width: 18, height: 18, color: '#0d9488' }} /></span>
            <span className="dv-smart-btn-value" style={{ color: '#0d9488', fontSize: '0.82rem' }}>DRS</span>
            <span className="dv-smart-btn-label">Balanceo carga</span>
          </button>
        )}
        {cluster?.id && (
          <DocSmartButton elementType="virt_cluster" elementId={cluster.id} elementName={form.name} companyId={companyId} />
        )}
      </div>
      )}

      {/* ── Body ── (en lectura se apaga de golpe con el fieldset) ── */}
      <div className="dv-body">
        <fieldset className={`dv-fieldset${isCards ? ' sf2-main' : ''}`} disabled={isCards ? false : !editing}>
        <div className={`dv-main${isCards ? ' sf2-main' : ''}`}>

          <FormCardsShell
            isCards={isCards}
            header={isCards ? (
              <FormCardsHead
                icon={<Squares2X2Icon style={{ width: 20, height: 20 }} />}
                title={form.name}
                onTitleChange={(v) => sf('name', v)}
                placeholder="Nombre del cluster…"
                editing={editing}
                saving={saving}
                onSave={handleSave}
                onCancel={onClose}
                onEdit={() => setEditing(true)}
                crumb={[typeInfo?.label || form.cluster_type, form.version, form.mgmt_ip].filter(Boolean).join(' › ')}
                badges={[
                  { text: statusInfo.label, color: form.status === 'active' ? '#16a34a' : form.status === 'degraded' ? '#f59e0b' : '#dc2626' },
                  ...(form.ha_enabled ? [{ text: 'HA' }] : []),
                  ...(form.is_hyperconverged ? [{ text: 'HCI' }] : []),
                  ...(form.drs_enabled ? [{ text: 'DRS' }] : []),
                ]}
                chips={[
                  { value: nodeCount, label: 'nodos', onClick: () => setTab('nodos') },
                  ...(form.mgmt_ip ? [{ value: form.mgmt_ip, label: 'IP gestión' }] : []),
                ]}
                extra={cluster?.id ? (
                  <DocSmartButton elementType="virt_cluster" elementId={cluster.id} elementName={form.name} companyId={companyId} variant="chip" />
                ) : null}
              />
            ) : (
              <>
              <input
                className="dv-record-title"
                value={form.name}
                onChange={e => sf('name', e.target.value)}
                placeholder="Nombre del cluster…"
              />
              </>
            )}
            tabs={(
              <div className="dv-tabs-nav">
                {(isCards
                  ? [{ id: 'general', label: 'General' }, { id: 'nodos', label: `Nodos (${nodeCount})` }]
                  : [{ id: 'general', label: 'General' }, { id: 'nodos', label: `Nodos (${nodeCount})` }]
                ).map(t => (
                  <TabButton
                    key={t.id}
                    className={`dv-tab-btn${tab === t.id ? ' active' : ''}`}
                    onClick={() => setTab(t.id)}
                  >
                    {t.label}
                  </TabButton>
                ))}
              </div>
            )}
          >
          <fieldset className="dv-fieldset" disabled={!editing}>

          {/* ── Tab: General ── */}
          {tab === 'general' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              {isCards && (
                <div className="sf2-hl" style={{ marginBottom: 12 }}>
                  <div className="sf2-kpi"><span>Tipo</span><b style={{ fontSize: '0.95rem' }}>{typeInfo?.label || form.cluster_type}</b></div>
                  <div className="sf2-kpi"><span>Versión</span><b style={{ fontSize: '0.95rem' }}>{form.version || '—'}</b></div>
                  <div className="sf2-kpi"><span>Nodos</span><b>{nodeCount}</b></div>
                  <div className="sf2-kpi"><span>Estado</span><b style={{ fontSize: '0.95rem' }}>{statusInfo.label}</b></div>
                </div>
              )}
              <Sf2Card isCards={isCards} title="Cluster" editing={editing}>
              <div className="dv-field-grid">
                <div className="dv-field-group">
                  <label className="dv-label">Tipo de cluster</label>
                  <select className="dv-select" value={form.cluster_type} onChange={e => sf('cluster_type', e.target.value)}>
                    {CLUSTER_TYPES.map(t => <option key={t.value} value={t.value}>{t.icon} {t.label}</option>)}
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Versión</label>
                  <input className="dv-input" value={form.version} onChange={e => sf('version', e.target.value)} placeholder="ej. 8.0" />
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Estado</label>
                  <select className="dv-select" value={form.status} onChange={e => sf('status', e.target.value)}>
                    <option value="active">Activo</option>
                    <option value="degraded">Degradado</option>
                    <option value="maintenance">Mantenimiento</option>
                    <option value="decom">Decom</option>
                  </select>
                </div>
                <div className="dv-field-group">
                  <label className="dv-label">Nodos configurados</label>
                  <input type="number" className="dv-input" value={form.total_nodes} onChange={e => sf('total_nodes', e.target.value)} placeholder="ej. 4" />
                </div>

                <div className="dv-section-header" style={{ gridColumn: '1 / -1' }}>Acceso de gestión</div>
                <div className="dv-field-group">
                  <label className="dv-label">IP de gestión</label>
                  <input className="dv-input" value={form.mgmt_ip} onChange={e => sf('mgmt_ip', e.target.value)} placeholder="ej. 10.0.0.20" />
                </div>
                <div className="dv-field-group" style={{ gridColumn: '1 / -1' }}>
                  <label className="dv-label">URL de gestión</label>
                  <input className="dv-input" value={form.mgmt_url} onChange={e => sf('mgmt_url', e.target.value)} placeholder="ej. https://vcenter.local/ui" />
                </div>

                <div className="dv-section-header" style={{ gridColumn: '1 / -1' }}>Capacidades</div>
                <div className="dv-field-group" style={{ gridColumn: '1 / -1' }}>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    {[
                      ['ha_enabled',        'Alta disponibilidad (HA)'],
                      ['drs_enabled',       'Balanceo de carga (DRS/DRM)'],
                      ['is_hyperconverged', 'Hiperconvergente (HCI)'],
                    ].map(([k, l]) => (
                      <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                        <input type="checkbox" checked={form[k]} onChange={e => sf(k, e.target.checked)} />
                        {l}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              </Sf2Card>
            </div>
          )}

          {/* ── Tab: Nodos ── */}
          {tab === 'nodos' && (
            <div className={`dv-tab-pane${isCards ? ' sf2-pane' : ''}`}>
              {!isEditing && (
                <div style={{ padding: '20px', background: '#f9fafb', borderRadius: 8, color: '#6b7280', textAlign: 'center' }}>
                  Guarda el cluster primero para poder añadir nodos (hypervisores).
                </div>
              )}
              {isEditing && (
                <>
                  {/* Add member row */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16, padding: '12px 16px', background: '#f9fafb', borderRadius: 8 }}>
                    <select
                      className="dv-select"
                      style={{ flex: 1 }}
                      value={addMember.hypId}
                      onChange={e => setAddMember(m => ({ ...m, hypId: e.target.value }))}
                    >
                      <option value="">— Seleccionar hypervisor —</option>
                      {hypervisors
                        .filter(h => !members.find(m => m.hypervisor_id === h.id))
                        .map(h => <option key={h.id} value={h.id}>{h.name}</option>)
                      }
                    </select>
                    <select
                      className="dv-select"
                      style={{ width: 160 }}
                      value={addMember.role}
                      onChange={e => setAddMember(m => ({ ...m, role: e.target.value }))}
                    >
                      {MEMBER_ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <button
                      className="dv-btn-save"
                      onClick={handleAddMember}
                      disabled={!addMember.hypId || addingMember}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      <PlusIcon style={{ width: 14, height: 14 }} />
                      {addingMember ? 'Añadiendo…' : 'Añadir nodo'}
                    </button>
                  </div>

                  {members.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af' }}>
                      Sin nodos asociados. Añade hypervisores al cluster.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Hypervisor</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>Rol</th>
                          <th style={{ textAlign: 'left', padding: '8px 12px', color: '#6b7280', fontWeight: 500 }}>IP gestión</th>
                          <th style={{ width: 40 }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map(m => (
                          <tr key={m.hypervisor_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '10px 12px', fontWeight: 500 }}>{m.hypervisor_name || m.hypervisor_id}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <span style={{ background: 'rgba(59,130,246,.16)', color: '#93c5fd', borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 500 }}>
                                {MEMBER_ROLES.find(r => r.value === m.member_role)?.label || m.member_role}
                              </span>
                            </td>
                            <td style={{ padding: '10px 12px', fontFamily: 'monospace', color: '#6b7280' }}>{m.hypervisor_ip || '—'}</td>
                            <td style={{ padding: '10px 12px' }}>
                              <button
                                onClick={() => handleRemoveMember(m.hypervisor_id)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}
                                title="Quitar del cluster"
                              >
                                <XMarkIcon style={{ width: 14, height: 14 }} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          )}

          </fieldset>
          <FormDirtyDock show={isCards && isDirty} saving={saving} onDiscard={handleDiscard} onSave={handleSave} />
          </FormCardsShell>
        </div>
        </fieldset>
        {/* Panel de actividad y comentarios: uno solo para todas las fichas. */}
        <CiActivityPanel
          companyId={companyId}
          ciType="virt_cluster"
          ciId={cluster?.id}
          tipoLegible="el cluster"
        />
      </div>
    </div>
  );
}
